import {useState, useCallback} from 'react'
import {ChevronDownIcon} from '@primer/octicons-react'
import type {Icon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import {ActionItem, type ActionItemProps} from './ActionItem'
import styles from './ActionItemGroup.module.css'
import {Action, type ActionConfig} from './Action'

interface ActionItemGroupProps {
  /** Leading icon displayed before the title */
  icon: Icon
  /** Group heading displayed in the header */
  title: string
  /** Optional count shown parenthetically after the title */
  count?: number
  /** Whether the group starts expanded (default: false) */
  defaultOpen?: boolean
  /** Action items rendered inside the collapsible body */
  items: ActionItemProps[]
  /** Optional config for a link or button action displayed in the header */
  action?: ActionConfig
  /** Optional visual variant controlling the leading icon color */
  variant?: 'default' | 'danger' | 'success' | 'warning' | 'info'
}

export function ActionItemGroup({
  icon: GroupIcon,
  title,
  count,
  action,
  defaultOpen = false,
  items,
  variant = 'default',
}: ActionItemGroupProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  const toggle = useCallback(() => {
    setIsOpen(prev => !prev)
  }, [])

  const displayTitle = count != null ? `${title} (${count})` : title

  return (
    <li className={styles.container}>
      <div className="d-flex flex-items-center">
        <button
          type="button"
          className={styles.header}
          aria-expanded={isOpen}
          onClick={toggle}
          aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${displayTitle}`}
        >
          <span className={styles.headerContent}>
            <span className={clsx(styles.leadingIcon, styles[variant])}>
              <GroupIcon size={16} />
            </span>
            <span className={styles.titleRow}>
              <span className={styles.title}>{displayTitle}</span>
              <ChevronDownIcon size={16} className={clsx(styles.chevron, !isOpen && styles.chevronCollapsed)} />
            </span>
          </span>
        </button>
        <span className={styles.trailing}>{action && <Action action={action} />}</span>
      </div>
      {isOpen && (
        // eslint-disable-next-line jsx-a11y/no-redundant-roles
        <ul role="list" className={styles.body}>
          {items.map(item => (
            <ActionItem key={`${item.title}-${item.description}`} {...item} className={styles.item} />
          ))}
        </ul>
      )}
    </li>
  )
}
